import { api } from '../../api';
import { studioApi, type StudioApp, type StudioDocument } from './studio-api';
import { record, text, unwrapTile } from './studio-content';
import type { StudioTarget } from './studio-navigation';

async function loadApp(id: number, signal: AbortSignal) {
  const app = await studioApi.app(id, signal);
  if (app.id !== id || !text(app.name) || !/^[a-z][a-z0-9_]{0,31}$/.test(text(record(app.entry).screen)))
    throw new Error('Не удалось открыть инструмент. Обновите каталог и попробуйте снова.');
  return app;
}

/** Несколько приложений экрана who (базовый кружок, второй инструмент, шаблоны формата «кружок»).
    1) Сопоставление экрана who: tools[] { who | source, app_id } или default_app_id — как у video и photo; сейчас
       документ его не публикует (docs/03-api.md §6.6), ветка ждёт админку.
    2) Цели строк «Все инструменты»: у сервера это только open_app { app_id } (MiniAppStudioPublicationPolicy.ValidateTool),
       шаблоны каталога целями инструментов не бывают. Одна цель — она; иначе строка с тем же who, единственная цель
       формата «кружок», строка раздела «Кружки» выше всех. */
async function circleApp(matches: StudioApp[], options: Record<string, unknown> | undefined, signal: AbortSignal) {
  const pick = (id: unknown) => (Number.isSafeInteger(id) ? matches.find(item => item.id === id) : undefined);
  const wanted = [text(options?.who), text(options?.source)].filter(Boolean);
  const document = await api.request<Record<string, unknown>>('/app/studio/screens/who', { signal }).catch((failure: unknown) => {
    if (signal.aborted) throw failure;
    return null;
  });
  const policy = record(document?.who);
  const published = Array.isArray(policy.tools) ? policy.tools.map(record) : [];
  const mapped = wanted.length ? published.filter(tool => wanted.includes(text(tool.who)) || wanted.includes(text(tool.source))) : [];
  const fromScreen = (mapped.length === 1 ? pick(mapped[0].app_id) : undefined) ?? pick(policy.default_app_id);
  if (fromScreen) return fromScreen;
  const rows = (await studioApi.allTools(signal)).items.map(unwrapTile).flatMap(tool => {
    const action = record(tool.action);
    const app = action.type === 'open_app' ? pick(action.app_id) : undefined;
    return app ? [{ tool, app }] : [];
  });
  const distinct = (list: typeof rows) => [...new Set(list.map(row => row.app))];
  const targets = distinct(rows);
  if (targets.length <= 1) return targets[0];
  const byWho = wanted.length ? distinct(rows.filter(row => wanted.includes(text(record(row.tool.entry).who)))) : [];
  if (byWho.length === 1) return byWho[0];
  const round = targets.filter(app => app.format === 'circle');
  if (round.length === 1) return round[0];
  const position = (row: (typeof rows)[number]) => (typeof row.tool.position === 'number' ? row.tool.position : Number.MAX_SAFE_INTEGER);
  const ordered = [...rows].sort((a, b) => Number(b.tool.section === 'circle') - Number(a.tool.section === 'circle') || position(a) - position(b));
  return ordered[0].app;
}

/** Идентификаторы берём из опубликованных документов, не из таблиц прототипа. */
export async function resolveStudioTarget(
  target: StudioTarget,
  signal: AbortSignal,
  preferredTitle?: string,
): Promise<{ app?: StudioApp; collection?: StudioDocument; view?: 'tools' | 'catalog' }> {
  if (target.view) return { view: target.view };
  if (target.appId) return { app: await loadApp(target.appId, signal) };
  if (target.screen === 'lipsync') target = { ...target, screen: 'circle' };
  // «Для маркетплейса» — подборка каталога, а не экран создания (решение владельца 07.10.2026):
  // прежний вход {screen:'market'} ведёт на её страницу, иначе он упирается в заглушку экрана.
  if (target.screen === 'market' && !target.collection) target = { ...target, screen: undefined, collection: 'market' };
  if (target.screen === 'swap' || (target.screen === 'who' && target.options?.drive === 'video'))
    target = { ...target, screen: 'dance', options: { ...target.options, preset: 'swap' } };
  // circle, lipsync и who — один экран сервера (MessagingStudioScreenContracts): вход who («Кружок с героем» главной,
  // подборки) выбирает приложение той же веткой, что и circle, иначе при двух приложениях who он упирается в ошибку.
  if (target.screen === 'who') target = { ...target, screen: 'circle' };
  if (target.collection) {
    let cursor: string | undefined;
    do {
      const page = await studioApi.list('app-collections', { cursor, limit: 50 }, signal);
      const found = page.items
        .map(unwrapTile)
        .find(item => item.code === target.collection || String(item.id) === target.collection);
      if (found) return { collection: found };
      cursor = page.next_cursor ?? undefined;
    } while (cursor);
    throw new Error('Подборка пока недоступна.');
  }
  const matches: StudioApp[] = [];
  let cursor: string | undefined;
  do {
    const page = await studioApi.list('apps', { cursor, limit: 50 }, signal);
    // Лента приходит конвертом items[] = { kind, app | collection | tool } (docs/03-api.md §6.2):
    // без разворачивания entry.screen не читается и вход не находится ни у одного инструмента.
    for (const item of page.items.map(unwrapTile)) {
      const screen = text(record(item.entry).screen);
      if (screen === target.screen || (target.screen === 'circle' && screen === 'who')) matches.push(item as StudioApp);
    }
    cursor = page.next_cursor ?? undefined;
  } while (cursor);
  if (target.screen === 'text' && !matches.some(item => item.name === preferredTitle)) {
    const document = await api.request<Record<string, unknown>>('/app/studio/screens/text', { signal });
    const policy = record(document.text);
    /* yt — «Текст из YouTube»: то же приложение расшифровки; tool:'yt' остаётся во входе, экран откроется со ссылкой */
    const aliases: Record<string, string> = {
      stt: 'transcription',
      yt: 'transcription',
      seo: 'product_description',
      pnew: 'prompt_create',
      pphoto: 'prompt_from_photo',
      pvideo: 'prompt_from_video',
      pfix: 'improve',
    };
    const requested = text(target.options?.mode) || text(target.options?.tool);
    const mode = aliases[requested] || requested || text(policy.default_mode) || 'transcription';
    const tools = Array.isArray(policy.tools)
      ? policy.tools.map(record).filter(t => t.mode === mode && Number.isSafeInteger(t.app_id))
      : [];
    const selected = tools.length === 1 ? matches.find(a => a.id === tools[0].app_id) : undefined;
    if (selected) return { app: selected };
  }
  if (target.screen === 'dance' && !matches.some(item => item.name === preferredTitle)) {
    const screen = await api.request<Record<string, unknown>>('/app/studio/screens/dance', { signal });
    const dance = record(screen.dance),
      preset = target.options?.preset === 'swap' ? 'swap' : 'dance';
    const tools = Array.isArray(dance.tools)
      ? dance.tools.map(record).filter(t => t.preset === preset && Number.isSafeInteger(t.app_id))
      : [];
    const selected = tools.length === 1 ? matches.find(a => a.id === tools[0].app_id) : undefined;
    if (selected) return { app: selected };
  }
  if (target.screen === 'video' && matches.length > 1 && !matches.some(item => item.name === preferredTitle)) {
    const screen = await api.request<Record<string, unknown>>('/app/studio/screens/video', { signal });
    const policy = record(screen.video);
    const aliases: Record<string, string> = { tx: 'text', ph: 'photo', vd: 'edit', cover: 'product', parallax: 'parallax' };
    const requested = text(target.options?.preset) || text(target.options?.mode);
    const mode = aliases[requested] || requested || text(policy.default_mode) || 'style';
    const tools = Array.isArray(policy.tools)
      ? policy.tools.map(record).filter(t => t.mode === mode && Number.isSafeInteger(t.app_id))
      : [];
    const selected = tools.length === 1 ? matches.find(item => item.id === tools[0].app_id) : undefined;
    if (selected) return { app: selected };
  }
  if (target.screen === 'photo' && matches.length > 1 && !matches.some(item => item.name === preferredTitle)) {
    const screen = await api.request<Record<string, unknown>>('/app/studio/screens/photo', { signal });
    const photo = record(screen.photo);
    const modes: Record<string, string> = {
      tx: 'generate',
      photo: 'style',
      ph: 'style',
      restore: 'restore',
      colorize: 'colorize',
      color: 'colorize',
      avatar: 'avatar',
      edit: 'edit',
      style: 'style',
      generate: 'generate',
    };
    const requested = text(target.options?.preset) || text(target.options?.mode);
    /* «Изменить фото» шаблона — режим «Из фото» без стиля (sty:'none'): у сервера это режим edit */
    const mode = (requested === 'ph' && text(target.options?.sty) === 'none' ? 'edit' : modes[requested]) || text(photo.default_mode) || 'style';
    const tools = Array.isArray(photo.tools)
      ? photo.tools.map(record).filter(tool => tool.mode === mode && Number.isSafeInteger(tool.app_id))
      : [];
    const selected = tools.length === 1 ? matches.find(item => item.id === tools[0].app_id) : undefined;
    if (selected) return { app: selected };
  }
  /* «Почистить запись» (keep, 2 C в минуту) и «Сменить голос в записи» (other, 10 C в минуту) — разные шаблоны: исполнитель
     выбирается по шаблону, вход mode он не читает, цену форма берёт у открытого шаблона (voice-model perMinute). Шаблон режима —
     блок voice документа экрана (docs/03-api.md §6.6); документа может не быть, тогда ниже — ошибка «несколько шаблонов». */
  if (target.screen === 'voice' && matches.length > 1 && !matches.some(item => item.name === preferredTitle)) {
    const screen = await api.request<Record<string, unknown>>('/app/studio/screens/voice', { signal }).catch((failure: unknown) => {
      if (signal.aborted) throw failure;
      return null;
    });
    const voice = record(screen?.voice);
    const isMode = (value: string) => value === 'keep' || value === 'other';
    const requested = text(target.options?.mode);
    const mode = isMode(requested) ? requested : isMode(text(voice.default_mode)) ? text(voice.default_mode) : 'other';
    const tools = Array.isArray(voice.tools)
      ? voice.tools.map(record).filter(tool => tool.mode === mode && Number.isSafeInteger(tool.app_id))
      : [];
    const selected = tools.length === 1 ? matches.find(item => item.id === tools[0].app_id) : undefined;
    if (selected) return { app: selected };
  }
  const circle = target.screen === 'circle' ? matches.filter(item => item.format === 'circle') : [];
  if (target.screen === 'circle' && matches.length > 1 && circle.length !== 1 && !matches.some(item => item.name === preferredTitle)) {
    const selected = await circleApp(matches, target.options, signal);
    if (selected) return { app: selected };
  }
  const app =
    matches.find(item => item.name === preferredTitle) ?? (circle.length === 1 ? circle[0] : matches.length === 1 ? matches[0] : undefined);
  if (app) return { app };
  throw new Error(
    matches.length
      ? 'Для этого действия доступно несколько шаблонов. Выберите подходящий в каталоге.'
      : 'Для этого действия пока нет доступного опубликованного инструмента.',
  );
}
